import AssetLoader from "@/components/AssetLoader/AssetLoader";
import AssetQuery from "@/components/AssetLoader/AssetQuery";
import Userlabel from "@/components/AvatarComponent/Userlabel";
import { PDFHighlight } from "@/components/PDFViewer/PDFViewer";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFMessage from "@/modules/abstract-ui/general/Message/BFMessage";
import EZGroup from "@/modules/ez-form/form-group/ez-form/EZGroup";
import UnitStruct from "@/redux/actions/struct/implemented/UnitStruct";
import DataBusDefaults from "@/services/DataBusDefaults";
import { hasValue } from "@/utils/Helpers";
import MQ from "@/utils/MatchQueryUtils";
import StringUtils from "@/utils/StringUtils";
import { useState } from "react";
import { Accordion } from "rsuite";
import InvoiceService from "../../InvoiceService";
import { Invoice, InvoiceChangeRequest } from "../../RAInterfaces";
import { getDunningLabel } from "../../RAUtils";
import RAInvoiceForm from "../InvoiceForm/RAInvoiceForm";
import RAInvoiceProcessHistory from "../InvoiceProcessHistory/RAInvoiceProcessHistory";
import RAInvoiceEntryView from "../RAInvoiceEntryView";
import { UploadInvoiceForm } from "../UploadIncomingInvoice/RAUploadInvoiceEntry";
import RAInvoiceCurrentTask from "./RAInvoiceCurrentTask";
import RAInvoicePaymentKPI from "./RAInvoicePaymentKPI";
// import "./RAInvoiceDetailsMain.scss";

interface RAInvoiceDetailsMainProps {
  invoice: Invoice;
  setHighlights: (highlights: PDFHighlight[]) => void;
  highlights: PDFHighlight[];
  reloadList: () => void;
}
const RAInvoiceDetailsMain = (props: RAInvoiceDetailsMainProps) => {
  const [initialState, setInitialState] = useState(
    InvoiceService.getInitialDataForInvoice(props.invoice)
  );

  return (
    <div className={`details-container`}>
      {props.invoice.data.status !== "init" && (
        <div className={`process-history-container`}>
          <RAInvoiceProcessHistory vertical invoice={props.invoice} />
        </div>
      )}
      <RAInvoicePaymentKPI invoice={props.invoice} />

      {props.invoice.data.deferred && props.invoice.data.status === "init" && (
        <BFMessage
          type="info"
          text={
            <>
              <div className={`__h3 padding-bottom-10`}>
                {i18n.t(
                  "ra:ApproveList.Entry.DeferredInvoice",
                  "Zurückgestellte Rechnung"
                )}
              </div>
              <div>{props.invoice.data.deferred.comment}</div>
              <div className={`__flex __align-items padding-top-10`}>
                <Userlabel id={props.invoice.data.deferred.user} />
                <span>
                  {StringUtils.formatDate(props.invoice.data.deferred.date)}
                </span>
              </div>
            </>
          }
        />
      )}

      {props.invoice?.data.correction?.isCorrection && (
        <div className="correction margin-bottom-10">
          <BFMessage
            type="info"
            text={
              <>
                <div className={`text`}>
                  {i18n.t(
                    "ra:ApproveList.Entry.IsCorrectionInvoice",
                    "Diese Rechnung ist eine Korrekturrechnung"
                  )}
                </div>
                <BFButton
                  appearance="link"
                  size="xs"
                  onClick={() =>
                    DataBusDefaults.route({
                      route: `__details/${props.invoice?.data.correction.linkedInvoice}`,
                      append: true,
                    })
                  }
                >
                  {i18n.t(
                    "ra:ApproveList.Entry.showOriginalInvoice",
                    "Zeige Originalrechnung"
                  )}
                </BFButton>
              </>
            }
          ></BFMessage>
        </div>
      )}
      {props.invoice?.data.correction?.isStorno && (
        <div className="storno margin-bottom-10">
          <BFMessage
            type="info"
            text={
              <>
                <div className={`text`}>
                  {i18n.t(
                    "ra:ApproveList.Entry.isStonroInvoice",
                    "Diese Rechnung ist eine Stornorechnung"
                  )}
                </div>
                <BFButton
                  appearance="link"
                  size="xs"
                  onClick={() =>
                    DataBusDefaults.route({
                      route: `__details/${props.invoice?.data.correction.linkedInvoice}`,
                      append: true,
                    })
                  }
                >
                  {i18n.t(
                    "ra:ApproveList.Entry.showOriginalInvoice",
                    "Zeige Originalrechnung"
                  )}
                </BFButton>
              </>
            }
          ></BFMessage>
        </div>
      )}

      <AssetQuery
        assetType={AssetTypes.Invoicing.Invoice}
        query={MQ.and(MQ.eq("data.reminderFor", props.invoice._id))}
        render={(data: Invoice[]) => {
          if (data.length === 0) {
            return null;
          }

          return (
            <BFMessage
              squished
              className={`margin-bottom-20`}
              type="info"
              title={
                <>
                  {data.length}{" "}
                  {data.length > 1
                    ? i18n.t(
                        "ra:InvoiceDetails.DunningsAvailable_multiple",
                        "Mahnungen vorhanden"
                      )
                    : i18n.t(
                        "ra:InvoiceDetails.DunningsAvailable_singualr",
                        "Mahnung vorhanden"
                      )}
                </>
              }
              text={
                <>
                  <Accordion
                    bordered
                    defaultActiveKey={1}
                    className="dunning-accordion"
                  >
                    {data.map((dunning, index) => (
                      <Accordion.Panel
                        header={
                          <div>
                            <span>
                              {getDunningLabel(dunning.data.reminderLevel)}
                            </span>
                            <span className={`padding-left-20 __sub-text`}>
                              {StringUtils.formatDate(
                                dunning.data.invoice.documentDate
                              )}
                            </span>
                          </div>
                        }
                        key={index}
                        eventKey={index + 1}
                      >
                        <div className={`padding-top-10 padding-bottom-10`}>
                          <RAInvoiceEntryView
                            invoice={dunning}
                            ignoreHistory
                            showInvoiceId
                            simpleView
                            hideCostCenter
                            hideInfoFields
                            hideRelations
                            hideTags
                            dense
                          />
                        </div>
                      </Accordion.Panel>
                    ))}
                  </Accordion>
                </>
              }
            />
          );
        }}
      />
      <AssetQuery
        assetType={AssetTypes.Invoicing.Invoice}
        query={MQ.and(
          MQ.eq("data.correction.linkedInvoice", props.invoice._id),
          MQ.eq("data.correction.isStorno", true)
        )}
        render={(data: Invoice[]) => {
          if (data.length === 0) {
            return null;
          }

          return (
            <BFMessage
              squished
              className={`margin-bottom-20`}
              type="info"
              title={
                <>
                  {data.length}{" "}
                  {data.length > 1
                    ? i18n.t(
                        "ra:InvoiceDetails.StornoAvailable_multiple",
                        "Storno-Rechnungen vorhanden"
                      )
                    : i18n.t(
                        "ra:InvoiceDetails.StornoAvailable_singualr",
                        "Storno-Rechnung vorhanden"
                      )}
                </>
              }
              text={
                <>
                  <Accordion
                    bordered
                    defaultActiveKey={1}
                    className="storno-accordion"
                  >
                    {data.map((storno, index) => (
                      <Accordion.Panel
                        header={
                          <div>
                            <span>
                              {StringUtils.formatCurrency(
                                storno.data.invoice.value.amount,
                                undefined,
                                undefined,
                                storno.data.invoice.value.currency
                              )}
                            </span>
                            <span className={`padding-left-20 __sub-text`}>
                              {StringUtils.formatDate(
                                storno.data.invoice.documentDate
                              )}
                            </span>
                          </div>
                        }
                        key={index}
                        eventKey={index + 1}
                      >
                        <div className={`padding-top-10 padding-bottom-10`}>
                          <RAInvoiceEntryView
                            invoice={storno}
                            ignoreHistory
                            showInvoiceId
                            simpleView
                            hideCostCenter
                            hideInfoFields
                            hideRelations
                            hideTags
                            dense
                          />
                        </div>
                      </Accordion.Panel>
                    ))}
                  </Accordion>
                </>
              }
            />
          );
        }}
      />

      {hasValue(props.invoice.data.changeRequestId) && (
        <AssetLoader
          assetType={AssetTypes.Invoicing.ChangeRequest}
          id={props.invoice.data.changeRequestId}
          render={(changeRequest: InvoiceChangeRequest) => (
            <BFMessage
              className={`margin-bottom-20`}
              type={"info"}
              title={i18n.t(
                "ra:InvoiceDetails.changeRequestOpenTitle",
                "Änderungsanfrage offen"
              )}
              text={
                <div>
                  <div>
                    {i18n.t(
                      "ra:InvoiceDetails.changeRequestOpenText",
                      "Die Bearbeitung dieser Rechnung ist aktuell nicht möglich, da eine Änderungsanfrage offen ist. Die Buchhaltung muss diese zuerst freigeben."
                    )}
                  </div>
                </div>
              }
            />
          )}
        />
      )}

      <div className={`id-label`}>
        {props.invoice?.id} -{" "}
        {UnitStruct.getUnitLabel(props.invoice?.data?.type)}
      </div>

      {props.invoice.data.status === "init" && (
        <UploadInvoiceForm
          asset={props.invoice}
          reloadList={props.reloadList}
          onFocus={(highlight) => props.setHighlights([highlight])}
        />
      )}
      {props.invoice.data.status !== "init" && (
        <EZGroup
          onSubmit={async (values: any) => {
            let invoice = null;
            if (props.invoice.data.booked) {
              invoice = await InvoiceService.requestInvoiceChange(
                props.invoice._id,
                values
              );

              setInitialState(
                InvoiceService.getInitialDataForInvoice(props.invoice)
              );
            } else {
              invoice = await InvoiceService.updateInvoice(
                props.invoice._id,
                values
              );
              setInitialState(InvoiceService.getInitialDataForInvoice(invoice));
            }
            setInitialState(InvoiceService.getInitialDataForInvoice(invoice));
          }}
          submitText={
            props.invoice.data.booked
              ? i18n.t(
                  "ra:InvoiceDetails.requestChange",
                  "Änderungen anfordern"
                )
              : i18n.t("Global.Buttons.save", "Speichern")
          }
          value={initialState}
        >
          {(renderProps) => (
            <RAInvoiceForm
              disableAll={hasValue(props.invoice.data.changeRequestId)}
              disableLinkedInvoiceChange
              ignoreCompare
              form={renderProps.form}
              invoice={props.invoice}
              onHighlight={(highlight) => {
                props.setHighlights(
                  highlight
                    ? Array.isArray(highlight)
                      ? highlight
                      : [highlight]
                    : []
                );
              }}
            />
          )}
        </EZGroup>
      )}

      {/* <FormStruct<any>
                initialValues={initialState}
                actionsOnChange
                submitText={i18n.t("Global.Buttons.save")}
                cancelText={i18n.t("Global.Buttons.cancel")}
                onSubmit={async (values: InvoiceBaseFormModel) => {
                }}
                onAbort={() => {
                  // update initial state - new object reference resets the form
                  setInitialState(
                    InvoiceService.getInitialDataForInvoice(props.invoice)
                  );
                }}
                render={(renderProps) => (
                )}
              /> */}
      <RAInvoiceCurrentTask invoice={props.invoice} />
    </div>
  );
};

export default RAInvoiceDetailsMain;
